import { v7 as uuidv7 } from 'uuid';
import { jwtDecode } from 'jwt-decode';
import {
  ACTIVITY_LOGGER_ENABLED,
  EVENT_NAMES,
  EVENT_TYPES,
  FLUSH_INTERVAL_MS,
  IDLE_TIMEOUT_MS,
  LOGOUT_FLUSH_TIMEOUT_MS,
  MAX_EVENTS_PER_BATCH,
  MAX_ID_LENGTH,
  RESOURCE_TYPES,
  RETRY_DELAYS_MS,
  SAVE_INTERVAL_MS,
  SLEEP_GAP_MS,
  STALE_VIEW_MS,
  TICK_INTERVAL_MS,
} from './constants';
import {
  enqueueEvent,
  getOpenView,
  getOtherOpenViews,
  isStorageAvailable,
  removeOpenView,
  saveOpenView,
  savePendingLogin,
  takePendingLogin,
} from './storage';
import { takePendingSignUps } from './signUp';
import { flushEvents } from './transport';
import {
  AuthHandlers,
  RoleResolver,
  EventName,
  EventType,
  FlushMode,
  IdleReason,
  OpenView,
  QueuedEvent,
  ResourceType,
  ViewDetails,
  ViewEndReason,
} from './types';

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

const toIso = (ms: number) => new Date(ms).toISOString();

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Keycloak's session id stays the same across token refreshes, so it identifies one login
const getSessionId = (accessToken: string | null): string | null => {
  if (!accessToken) return null;
  try {
    const claims = jwtDecode<{ sid?: string; session_state?: string }>(accessToken);
    return (claims.sid ?? claims.session_state)?.slice(0, MAX_ID_LENGTH) ?? null;
  } catch {
    return null;
  }
};

// Dashboards are sent as DASHBOARD_VIEW with their submodule id; any other page (user management,
// deal setup, ...) as PAGE_VIEW with its route
const getViewTarget = ({
  route,
  submoduleId,
}: ViewDetails): { eventType: EventType; resourceType: ResourceType; resourceId: string } =>
  submoduleId
    ? {
        eventType: EVENT_TYPES.DASHBOARD_VIEW,
        resourceType: RESOURCE_TYPES.DASHBOARD,
        resourceId: submoduleId.slice(0, MAX_ID_LENGTH),
      }
    : {
        eventType: EVENT_TYPES.PAGE_VIEW,
        resourceType: RESOURCE_TYPES.PAGE,
        resourceId: route.slice(0, MAX_ID_LENGTH),
      };

const buildViewEvent = (
  eventName: EventName,
  view: OpenView,
  eventDetails: Record<string, unknown>,
  at: number,
): QueuedEvent => ({
  eventId: uuidv7(),
  userId: view.userId,
  sessionId: view.sessionId || undefined,
  ...getViewTarget(view),
  eventName,
  occurredAt: toIso(at),
  // viewId tells apart the same dashboard open in two tabs of one session
  eventDetails: { viewId: view.viewId, route: view.route, ...eventDetails },
});

// Focus inside an embedded iframe blurs the window but the user is still on the dashboard
const isPageActive = (): boolean =>
  document.visibilityState === 'visible' &&
  (document.hasFocus() || document.activeElement?.tagName === 'IFRAME');

const isSameView = (a: ViewDetails, b: ViewDetails) =>
  a.route === b.route && a.submoduleId === b.submoduleId;

class ActivityTracker {
  // A new id per page load, so a duplicated or reloaded tab never shares open view state
  private readonly tabId = uuidv7();

  private userId: string | null = null;

  private sessionId = '';

  private running = false;

  private view: OpenView | null = null;

  // View to reopen when the user comes back after an idle timeout or a background load
  private resumeDetails: ViewDetails | null = null;

  private lastInputAt = Date.now();

  private lastTickAt = Date.now();

  private lastSavedAt = 0;

  private retryCount = 0;

  private auth: AuthHandlers | null = null;

  // Role of the user in a module (ADMIN, VIEWER or FULLACCESS), recorded on user actions
  private getRole: RoleResolver | null = null;

  private tickTimer: ReturnType<typeof setInterval> | null = null;

  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /* Public API */

  // Called on the auth callback. The login is emitted once the user id is known.
  recordLogin(accessToken: string): void {
    if (!ACTIVITY_LOGGER_ENABLED) return;
    savePendingLogin({ occurredAt: toIso(Date.now()), sessionId: getSessionId(accessToken) });
  }

  start(
    userId: string,
    accessToken: string | null,
    auth: AuthHandlers,
    getRole: RoleResolver,
  ): void {
    // Without localStorage nothing can be queued or recovered
    if (!ACTIVITY_LOGGER_ENABLED || !userId || !isStorageAvailable()) return;
    if (this.running && this.userId === userId) return;
    if (this.running) this.stop();

    this.userId = userId;
    this.auth = auth;
    this.getRole = getRole;
    // Falls back to an id for this page load if the token has no session claim
    this.sessionId = getSessionId(accessToken) ?? uuidv7();
    this.running = true;
    this.lastInputAt = Date.now();
    this.lastTickAt = Date.now();

    const login = takePendingLogin();
    if (login) {
      this.queue({
        eventId: uuidv7(),
        userId,
        sessionId: login.sessionId ?? this.sessionId,
        eventType: EVENT_TYPES.USER_SESSION,
        eventName: EVENT_NAMES.LOGIN,
        occurredAt: login.occurredAt,
      });
    }

    // Sign-ups recorded on this browser before the user had a token. They join the queue and go out
    // with the next batch: sent on their own, the event tracker rejected them (401 3004).
    takePendingSignUps(accessToken).forEach((event) =>
      this.queue({ ...event, userId, sessionId: this.sessionId }),
    );

    this.recoverOrphanedViews();
    this.addListeners();
    this.tickTimer = setInterval(this.tick, TICK_INTERVAL_MS);

    // Sends anything left from a previous visit (crash, expired token, failed send)
    this.scheduleFlush(0);
  }

  // Called when the route changes and the new view is mounted
  setView(details: ViewDetails): void {
    if (!this.running) return;
    if (this.view && isSameView(this.view, details)) return;

    if (this.view) {
      this.endView('route_change', this.view.inactiveSince ?? Date.now());
    }

    if (isPageActive()) {
      this.resumeDetails = null;
      this.startView(details);
    } else {
      // Opened in a background tab: the view starts when the user arrives
      this.resumeDetails = details;
    }
  }

  // A user action (ROAS projections, investor report). Recorded when the action's API call
  // succeeds, with the user's role in the module open in this tab (eventDetails.role).
  trackAction({
    eventType,
    eventName,
    resourceType,
    resourceId,
    eventDetails = {},
  }: {
    eventType: EventType;
    eventName: EventName;
    resourceType?: ResourceType;
    resourceId?: string;
    eventDetails?: Record<string, unknown>;
  }): void {
    if (!this.running || !this.userId) return;

    // The module of the open view, or of the view waiting to resume after an idle timeout
    const submoduleId = this.view?.submoduleId ?? this.resumeDetails?.submoduleId ?? null;

    this.queue({
      eventId: uuidv7(),
      userId: this.userId,
      sessionId: this.sessionId,
      eventType,
      eventName,
      resourceType,
      resourceId: resourceId?.slice(0, MAX_ID_LENGTH),
      occurredAt: toIso(Date.now()),
      eventDetails: { ...eventDetails, role: this.getRole?.(submoduleId) ?? null },
    });
  }

  // Ends the open view, records the logout and stops tracking. A manual logout waits (up to 2s)
  // for the events to be sent, because the event tracker rejects them once the pvx-core session
  // has ended. After a session expiry the token no longer works, so the events wait for the
  // next login.
  async logout(isManual: boolean): Promise<void> {
    if (!this.running || !this.userId || !this.auth) return;
    const { userId, sessionId, auth } = this;

    this.closeView(isManual ? 'logout' : 'session_expired');
    this.queue({
      eventId: uuidv7(),
      userId,
      sessionId,
      eventType: EVENT_TYPES.USER_SESSION,
      eventName: EVENT_NAMES.LOGOUT,
      occurredAt: toIso(Date.now()),
      eventDetails: { isManual },
    });
    this.stop();

    if (isManual) {
      // The user is already logging out, so a failed refresh doesn't send them to log in
      const logoutAuth = { ...auth, navigateToAuth: () => undefined };
      await Promise.race([
        flushEvents(userId, 'keepalive', logoutAuth),
        wait(LOGOUT_FLUSH_TIMEOUT_MS),
      ]);
    }
  }

  stop(): void {
    this.removeListeners();
    if (this.tickTimer) clearInterval(this.tickTimer);
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.tickTimer = null;
    this.flushTimer = null;
    this.running = false;
    this.view = null;
    this.resumeDetails = null;
    this.retryCount = 0;
  }

  /* Views */

  private startView(details: ViewDetails, at = Date.now()): void {
    if (!this.userId) return;

    this.view = {
      ...details,
      viewId: uuidv7(),
      userId: this.userId,
      sessionId: this.sessionId,
      startedAt: at,
      lastActiveAt: at,
      inactiveSince: null,
      savedAt: at,
    };
    this.lastInputAt = at;
    this.saveView(at);

    this.queue(buildViewEvent(EVENT_NAMES.VIEW_STARTED, this.view, {}, at));
  }

  private endView(endReason: ViewEndReason, activeUntil: number, at = Date.now()): void {
    if (!this.view) return;

    this.queue(
      buildViewEvent(
        EVENT_NAMES.VIEW_ENDED,
        this.view,
        { endReason, activeUntil: toIso(activeUntil) },
        at,
      ),
    );

    removeOpenView(this.tabId);
    this.view = null;
  }

  // VIEW_IDLE is always followed by VIEW_ENDED. Both are stamped with the exact moment the
  // timeout was reached, however late it is detected (throttled or frozen tab).
  private idleTimeout(idleReason: IdleReason, inactiveSince: number): void {
    if (!this.view) return;

    const timedOutAt = inactiveSince + IDLE_TIMEOUT_MS;
    const { route, submoduleId } = this.view;

    this.queue(
      buildViewEvent(
        EVENT_NAMES.VIEW_IDLE,
        this.view,
        { idleReason, inactiveSince: toIso(inactiveSince) },
        timedOutAt,
      ),
    );
    this.endView('idle_timeout', inactiveSince, timedOutAt);
    this.resumeDetails = { route, submoduleId };
  }

  // Ends the open view for a close, logout or session expiry. If the idle timeout was already
  // reached but not yet detected, it is recorded as an idle timeout instead.
  private closeView(endReason: ViewEndReason): void {
    if (!this.view) return;

    const now = Date.now();
    const { inactiveSince } = this.view;

    if (inactiveSince !== null && now - inactiveSince >= IDLE_TIMEOUT_MS) {
      this.idleTimeout('out_of_focus', inactiveSince);
    } else if (inactiveSince === null && now - this.lastInputAt >= IDLE_TIMEOUT_MS) {
      this.idleTimeout('no_input', this.lastInputAt);
    } else {
      this.endView(endReason, inactiveSince ?? now, now);
    }
  }

  private saveView(at = Date.now()): void {
    if (!this.view) return;
    if (this.view.inactiveSince === null) this.view.lastActiveAt = at;
    saveOpenView(this.tabId, this.view);
    this.lastSavedAt = at;
  }

  // Views left open by tabs that closed or crashed without ending them
  private recoverOrphanedViews(): void {
    const now = Date.now();

    getOtherOpenViews(this.tabId).forEach(({ tabId, view }) => {
      if (now - view.savedAt < STALE_VIEW_MS) return; // another live tab

      enqueueEvent(
        buildViewEvent(
          EVENT_NAMES.VIEW_ENDED,
          view,
          { endReason: 'recovered', activeUntil: toIso(view.inactiveSince ?? view.lastActiveAt) },
          now,
        ),
      );
      removeOpenView(tabId);
    });
  }

  /* Activity */

  private markInactive = (at = Date.now()): void => {
    if (!this.view || this.view.inactiveSince !== null) return;
    this.view.inactiveSince = at;
    this.saveView(at);
  };

  private markActive = (): void => {
    if (!this.running || !isPageActive()) return;

    // Detects timeouts reached while the tab was throttled, frozen or the device asleep
    this.tick();

    const now = Date.now();
    this.lastInputAt = now;

    if (this.view) {
      if (this.view.inactiveSince !== null) {
        this.view.inactiveSince = null;
        this.saveView(now);
      }
      return;
    }

    // The user is back after an idle timeout, or arrived at a tab loaded in the background
    if (this.resumeDetails) {
      const details = this.resumeDetails;
      this.resumeDetails = null;
      this.startView(details, now);
    }
  };

  private tick = (): void => {
    const now = Date.now();
    const gap = now - this.lastTickAt;
    this.lastTickAt = now;

    if (!this.view) return;
    const { inactiveSince, route, submoduleId } = this.view;

    // A tab frozen for a long time looks closed to other tabs, which end its view as
    // 'recovered'. Ending it again here would send a second end for the same view.
    if (!getOpenView(this.tabId)) {
      this.view = null;
      this.resumeDetails = { route, submoduleId };
      return;
    }

    // Timers stop while the device sleeps, so a long gap on a visible, active page means sleep.
    // Gaps on hidden pages come from browser throttling and are already covered by inactiveSince.
    if (inactiveSince === null && document.visibilityState === 'visible' && gap > SLEEP_GAP_MS) {
      if (gap >= IDLE_TIMEOUT_MS) {
        this.idleTimeout('device_sleep', now - gap);
        return;
      }
    }

    if (inactiveSince !== null && now - inactiveSince >= IDLE_TIMEOUT_MS) {
      this.idleTimeout('out_of_focus', inactiveSince);
      return;
    }

    if (inactiveSince === null && now - this.lastInputAt >= IDLE_TIMEOUT_MS) {
      this.idleTimeout('no_input', this.lastInputAt);
      return;
    }

    if (now - this.lastSavedAt >= SAVE_INTERVAL_MS) {
      this.saveView(now);
    }
  };

  /* Browser events */

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.markInactive();
      // Sends queued events now, so they don't wait for the next visit if the tab never returns
      this.sendNow('keepalive');
    } else {
      this.markActive();
    }
  };

  private onBlur = (): void => {
    const blurredAt = Date.now();
    // activeElement updates after blur, so focus moving into an iframe is checked on the next task
    setTimeout(() => {
      if (!isPageActive()) this.markInactive(blurredAt);
    }, 0);
  };

  private onInput = (): void => {
    if (this.view && this.view.inactiveSince === null) {
      this.lastInputAt = Date.now();
      return;
    }
    this.markActive();
  };

  private onPageHide = (): void => {
    if (!this.running) return;

    if (this.view) {
      const { route, submoduleId } = this.view;
      this.closeView('page_closed');
      // Reopened if the page comes back from the back/forward cache
      this.resumeDetails = { route, submoduleId };
    }
    this.sendNow('closing');
  };

  // Page restored from the back/forward cache after pagehide ended its view
  private onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) this.markActive();
  };

  private addListeners(): void {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.markActive);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    INPUT_EVENTS.forEach((type) =>
      window.addEventListener(type, this.onInput, { capture: true, passive: true }),
    );
  }

  private removeListeners(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.markActive);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    INPUT_EVENTS.forEach((type) =>
      window.removeEventListener(type, this.onInput, { capture: true }),
    );
  }

  /* Queue and sending */

  private queue(event: QueuedEvent): void {
    const queued = enqueueEvent(event);

    // The API takes up to 100 events per request: a full batch is sent straight away
    if (queued >= MAX_EVENTS_PER_BATCH && this.running && this.retryCount === 0) {
      this.scheduleFlush(0);
    }
  }

  private scheduleFlush(delay: number): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(this.runScheduledFlush, delay);
  }

  // Sends every 30s. After a batch is sent or dropped, the next one goes straight away in case
  // more are waiting. Temporary server errors keep the events and back off (5s, 15s, then 60s).
  private runScheduledFlush = async (): Promise<void> => {
    if (!this.running || !this.userId || !this.auth) return;

    this.recoverOrphanedViews();
    const result = await flushEvents(this.userId, 'normal', this.auth);
    if (!this.running) return;

    if (result === 'retry') {
      const delay = RETRY_DELAYS_MS[Math.min(this.retryCount, RETRY_DELAYS_MS.length - 1)];
      this.retryCount += 1;
      this.scheduleFlush(delay);
      return;
    }

    this.retryCount = 0;
    this.scheduleFlush(result === 'accepted' || result === 'dropped' ? 0 : FLUSH_INTERVAL_MS);
  };

  // Hidden or closing page: sends without waiting for the schedule
  private sendNow(mode: FlushMode): void {
    if (this.userId && this.auth) flushEvents(this.userId, mode, this.auth);
  }
}

const activityTracker = new ActivityTracker();

export default activityTracker;
